import { httpApi } from '@nucleus-care/nucleuscare-backend-client';
import { create } from 'zustand';

export type PatientLink = {
  id: string;
  label: string;
  url: string;
};



export interface PatientDetail {
  PatientID: string;
  TimeFormat12: boolean;
  PhotosCount: number;
  FirstName: string;
  LastName: string;
  AccountEnableBiometrics: boolean;
  EnableBiometrics: boolean;
  AccountEnableBluetoothButtons: boolean;
  EnableBluetoothButtons: boolean;
  Telephone: string;
  Address: string;
  DeviceCallShortcut: string | null;
  EmergencyContactName: string | null;
  EmergencyContactTelephone: string | null;
  DateOfBirth: string | null;
  ShowWeather: boolean | null;
  AccountShowCallButtonsBar: boolean;
  ShowCallButtonsBar: boolean;
  AccountShowCallButton: boolean;
  ShowCallButton: boolean;
  AccountShowCallButton2: boolean;
  ShowCallButton2: boolean;
  AccountShowCheckInButton: boolean;
  ShowCheckInButton: boolean;
  AccountShowEmergencyButton: boolean;
  ShowEmergencyButton: boolean;
  AccountEnableAutoAnswer: boolean;
  EnableAutoAnswerForFamily: boolean;
  EnableAutoAnswerForFamilyApp: boolean;
  EnableAutoAnswer: boolean;
  AccountEnableAppIncomingCall: boolean;
  EnableAppIncomingCall: boolean;
  CallPhoneFromDevice: boolean;
  AccountEnableMemoryBox: boolean;
  EnableMemoryBox: boolean;
  EnableBlankMemoryBox: boolean;
  MemoryBoxButtonText: string;
  PhoneCallFromDeviceNumber: string | null;
  ForceLandscapeVideo: boolean;
  UseDefaultSchedule: boolean;
  EnableCallButtonSchedule: boolean;
  EnableEmergencyButtonSchedule: boolean;
  EnableVoiceMessage: boolean;
  EnableImpairedRingtone: boolean;
  ZipCode: string | null;
  LocaleTag: string;
  EnableExternalApps: boolean;
  AccountExternalAppsEnabled: boolean;
  AccountAudioCallsOnly: boolean;
  AllowVideoForAccountAudioCallsOnly: boolean;
  TimezoneString: string;
  ImageUrl: string;
  ImageThumb: string;
  TelephoneCountryCode: string;
  CountryCode: string;
  QuickNote: string;
  Links?: PatientLink[];
}

type Store = {
  details?: PatientDetail;
  updatingQuickNote?: boolean;
  fetching?: boolean;
  addingLink?: boolean;
  removingLink?: boolean;
  patientLinks?: PatientLink[];
  setDetails: (details: PatientDetail) => void;
  updateQuickNote: (quickNote: string) => void;
  createPatientLink: (link: PatientLink) => void;
  deletePatientLink: (linkId: string) => void;
  fetch: () => Promise<void>;
};

export const patientDetailStore = create<Store>((set, get) => ({
  details: undefined,
  setDetails: (details: PatientDetail) => set({ details, patientLinks: details?.Links || [] }),
  updateQuickNote: async (quickNote: string) => {
    const { PatientID } = get().details;
    set({ updatingQuickNote: true });
    try {
      await httpApi.put(`/patient/${PatientID}/quick-note`, { quickNote });
    } catch (error) {
      console.error('Error updating quick note:', error);
      set({ updatingQuickNote: false });
      return;
    }
    set({ details: { ...get().details, QuickNote: quickNote }, updatingQuickNote: false });
  },
  fetch: async () => {
    const { PatientID } = get().details;
    set({ fetching: true });
    try {
      const response = await httpApi.get(`/patient/${PatientID}/details`);
      set({ details: response.data, fetching: false });
    } catch (error) {
      console.error('Error fetching patient details:', error);
      set({ fetching: false });
      return;
    }
  },
  createPatientLink: async (link: PatientLink) => {
    const { PatientID } = get().details;
    try {
      set({ addingLink: true });
      const response = await httpApi.post(`/patient/${PatientID}/links`, link);
      set(state => ({
        patientLinks: [...(state.patientLinks || []), response.data],
        addingLink: false,

      }));
    } catch (error) {
      console.error('Error creating patient link:', error);
      set({ addingLink: false });
    }
  },
  deletePatientLink: async (linkId: string) => {
    const { PatientID } = get().details;
    try {
      set({ removingLink: true });
      await httpApi.delete(`/patient/${PatientID}/links/${linkId}`);
      set(state => ({
        patientLinks: state.patientLinks?.filter(link => link.id !== linkId),
        removingLink: false,
      }));
    } catch (error) {
      console.error('Error deleting patient link:', error);
      set({ removingLink: false });
    }
  },
}));
